import { useLocation, useMatches, useMatchRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { MainLayoutHeaderTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import { Header, XStack, YStack } from '@/components/ui';
import {
  pageHeaderTitleTunnel,
  pageHeaderTunnel,
} from '@/design-system-v2/page-header-tunnel';
import { ArenaHeader } from '@/features/arena/arena-header';
import { MemoryViewSwitcher } from '@/features/echonotes/MemoryViewSwitcher';
import { JournalsHeader } from '@/features/journal/journal-header';
import { ProjectPanelHeader } from '@/features/projects/projects';
import { RoutinesHeader } from '@/features/routines/routines-header';
import { tabStore } from '@/features/tabs/tabs.store';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { isWindows } from '@/utils';

import { ChatHeader } from './Chat/ChatHeader';

const PanelHeader = observer(() => {
  const location = useLocation();
  const matchRoute = useMatchRoute();
  const path = `/${location.pathname.split('/')[1]}`;
  const pageOwnsTitle = useMatches({
    select: (matches) =>
      matches[matches.length - 1]?.staticData.headerTitle === 'page',
  });

  const needsWindowControls =
    isWindows() && !tabStore.showTabBar && !uiStore.chromeless;
  const skipsPanelHeader =
    !!matchRoute({ to: '/sprites/$spriteId' }) ||
    !!matchRoute({ to: '/bird-game' }) ||
    uiStore.chromeless;
  if (!needsWindowControls && skipsPanelHeader) return null;

  return (
    <YStack
      className={cn(
        'relative h-[var(--panel-header-height)] shrink-0 [container-type:inline-size]'
      )}
    >
      <XStack className="h-full items-center justify-start px-4">
        <XStack className="gap-2 justify-start items-center w-full">
          <XStack className="min-w-0 flex-1">
            {(() => {
              if (pageOwnsTitle)
                return (
                  <Header
                    left={<pageHeaderTitleTunnel.Out />}
                    right={<pageHeaderTunnel.Out />}
                  />
                );
              switch (path) {
                case '/chats':
                case '/':
                  return <ChatHeader />;
                case '/routines':
                  return <RoutinesHeader />;
                case '/journal':
                  return <JournalsHeader />;
                case '/arena':
                  return <ArenaHeader />;
                case '/echonotes':
                case '/world-model':
                  return <Header left={<MemoryViewSwitcher />} />;
                case '/projects':
                  return <ProjectPanelHeader />;
                case '/integrations':
                case '/meetings':
                case '/sprites':
                case '/settings':
                  return (
                    <Header
                      left={<MainLayoutHeaderTunnel.Out />}
                      right={<pageHeaderTunnel.Out />}
                    />
                  );
                default:
                  return <Header right={<pageHeaderTunnel.Out />} />;
              }
            })()}
          </XStack>
        </XStack>
      </XStack>
    </YStack>
  );
});

export { PanelHeader };
